<template>
  <div>
    <portal to="dash-modal">
      <ProductPickerModal 
        v-if="showProductModal"
        @close="closeProductModal"
        @products-selected="selectProducts"
        @product-created="selectProduct"
        @group-created="$emit('refresh-groups')"
      />
    </portal>
    <b-input-group>
      <b-form-textarea 
        ref="line-input"
        v-model="input_text"
        autocomplete="off"
        :placeholder="$gettext('Product or service name')" type="text"
        @input.native="onTextInput"
        @keydown.native="onKeydownInput"
        @blur="onBlur"
        @focus="onFocus"
        max-rows="10"
        class="line-input-textarea"
      />
      <template v-if="withProducts" #append>
        <GekkoButton
          :disabled="productDisabled"
          :icon="'fa-solid fa-basket-shopping-plus'"
          class="input-append-btn invoice-line-product-btn"
          @click="showProductModal = true"
        />
      </template>
    </b-input-group>
    
    <div v-if="withProducts && productList.length > 0" class="position-relative">
      <div class="product-list-wrap">
        <overlay-scrollbars :options="scrollBarsOptions" ref="dropselectScrollBar">
          <ul>
            <li v-for="(product, index) in productList" :key="product.id" :class="{ selected: (selectedIndex === index)}" @click="selectProduct(product)">
              <span v-if="getProductGroupName(product.group)">{{ getProductGroupName(product.group) }} - </span>{{ product.description }}
            </li>
          </ul>
        </overlay-scrollbars>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import { debounce } from "@/utils/";

import ProductService from "@/store/services/product.service";

import ProductPickerModal from "@/components/invoices/ui/ProductPickerModal"
import GekkoButton from "@/components/GekkoButton";

export default {
  name: "LineDescriptionInput",
  components: {
    GekkoButton,
    ProductPickerModal
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    lineIndex: {
      type: Number,
      default: null
    },
    withProducts: {
      type: Boolean,
      default: true
    },
    productDisabled: {
      type: Boolean,
      default: true
    },
  },
  data () {
    return {
      productList: [],
      selectedIndex: -1,
      enableSearch: false,
      showProductModal: false,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
        callbacks : {
            onScroll: this.handleScroll
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      productGroups: "invoices/getProductGroups"
    }),
    input_text: {
      get () { return this.value },
      set (input_text) { this.$emit('input', input_text)  }
    },
  },
  watch: {
    value(newVal) {
      if (this.withProducts && newVal.length > 2) {
        this.debouncedSearch(newVal)
      }
    }
  },
  created () {
    if (this.withProducts) {
      this.debouncedSearch = debounce(search_term => {
        if (this.enableSearch) {
          ProductService.list({ search: search_term, company_profile: this.currentCompany.id, archived: 'False' }).then((response) => {
            this.productList = response.data
            this.selectedIndex = -1 // reset selected Index
          })
        }
      }, 300)
    }
  },
  methods: {
    getProductGroupName (group_id) {
      let product_group_name = null
      if (group_id &&this.productGroups.length > 0) {
        const product_group = this.productGroups.find(group => group.id == group_id)
        if (product_group) {
          product_group_name = product_group.name
        }
      }
      return product_group_name
    },
    closeProductModal () {
      this.showProductModal = false
    },
    onTextInput () {
      this.enableSearch = true
    },
    onKeydownInput (ev) {
      if (this.withProducts) {
        const input_el = this.$refs['line-input'].$el
        const val_len = input_el.value.length
        switch (ev.key) {
          case 'ArrowDown':
            if (this.selectedIndex < (this.productList.length - 1)) {
              this.selectedIndex++
            }
            break;
          case 'ArrowUp':
            // preventDefault to avoid moving cursor to pos 0
            ev.preventDefault()
            if (this.selectedIndex > 0) {
              this.selectedIndex--
            }
            break;
          case 'Enter':
            this.selectProduct(this.productList[this.selectedIndex])
            break;
        }
      }
    },
    onBlur () {
      if (this.withProducts) {
        setTimeout(() => this.resetSuggestionList(), 300) // :-(
      }
    },
    onFocus () {
      this.enableSearch = true
      // if (this.input_text.length > 2) {
      //   this.debouncedSearch(this.input_text)
      // }
    },
    selectProducts (products) {
      this.selectProduct(products[0]) // new modal returns an array
    },
    selectProduct(product) {
      this.resetSuggestionList()
      this.enableSearch = false
      if (product) {
        this.input_text = product.description
        this.$nextTick(() => {
          this.$emit('product-selected', { line_index: this.lineIndex, product: product })
        })
      }
    },
    resetSuggestionList () {
      this.productList = []
      this.selectedIndex = -1
    }
  }
}
</script>

<style lang="scss">
.product-list-wrap {
  .os-host { height: 100%; }
  .os-content { padding: 0 !important; }
  &.os-host-overflow-y {
    .os-content { padding-right: 0.5rem !important; }
  }
}
</style>
<style lang="scss" scoped>
.product-list-wrap {
  position: absolute;
  top: 0;
  left: 0;
  background: white;
  border: 1px solid $colorBorderDash;
  border-radius: 6px;
  z-index: 10;
  width: 100%;
  height: 300px;
  max-height: 250px;
  ul {
    padding: 0;
    margin: 0;
    list-style: none;
    font-size: 1rem;
    padding: 0.25rem 0;
    li {
      padding: 0.25rem 0.75rem;
      cursor: pointer;
      &:hover:not(.selected) {
        background: rgba(10,10,10,.04);
      }
      &.selected {
        background: rgba(10,10,10,.12);
      }
    }
  }
}

.invoice-line-product-btn {
  border-top-right-radius: $radiusMd;
  border-bottom-right-radius: $radiusMd;

  border-left: 0px;
  color: $colorBlue;
  box-shadow: $inputBoxShadow !important;

  border: 0px;
  box-shadow: $inputBoxShadow !important;
  background-color: $inputBackgroundColor;

  &:hover, &:active, &:focus {
    background-color: $inputBackgroundColor !important;
    border-color: $inputBorderColor !important;
    color: $colorBlue !important;
    opacity: 1;
  }
  &.btn.disabled {
    box-shadow: $inputBoxShadow !important;
    background-color: $inputBackgroundColor;
    color: $colorTextLight !important;
    opacity: 1 !important;
  }
}

.gk-input-light {
  .input-append-btn {
    background: white;
    border: 1px solid $inputBorderColor;
    &:hover, &:active, &:focus {
      background: white !important;
    }
  }
}

.line-input-textarea {
  // height: calc(2.25rem + 2px);
  min-height: calc(2.25rem + 2px);
}

</style>